<script setup lang="ts">
import { computed, useEventListener, useTemplateRef, useElementSize, whenever, definePageMeta, usePreviewBridge, useGameList, previewGame } from '#imports'
// Namespace import, not `{ z }`: Vite drops the re-exported `z` binding, so the
// named form resolves to undefined under the Nuxt test environment.
import * as z from 'zod/v4'

definePageMeta({
    layout: false,
    hideOverlayContainer: true,
    preview: true,
})

/**
 * Mirrored by TileGen's preview payload constant — the keys below are the contract
 * between the two, so update both together. Same shape as `/preview/hero-rotator`;
 * images are data URLs because TileGen drafts sit behind Cloudflare Access.
 */
const DataSchema = z.looseObject({
    name: z.string().default(''),
    logo: z.string().default(''),
    background: z.string().default(''),
    /** Accepted for contract parity with the rotator; the lobby tile has no fade colour. */
    color: z.string().optional(),
})

const { data, send } = usePreviewBridge(DataSchema)

const game = computed(() => previewGame(data.value?.data?.name ?? '', data.value?.data?.color))

// GameTile resolves its list-scoped state through useGameTile, which needs this provider.
useGameList(() => [game.value])

// The slot is the embedder's width; the tile keeps the lobby ratio, so the frame
// reports its height and lets the width be.
const slot = useTemplateRef<HTMLDivElement>('slot')
const { height } = useElementSize(slot)

whenever(height, h => send({ type: 'resize', payload: { height: `${h}px`, width: '100%' } }))

// The tile opens a game on click; swallow it before it reaches the handler.
if (import.meta.client) {
    useEventListener(window, 'click', (event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
    }, { capture: true })
}
</script>

<template>
    <UIApp>
        <div v-if="!data">
            <UILoader bg>
                Waiting for data
            </UILoader>
        </div>

        <template v-else>
            <div v-if="data.error">
                <pre>{{ data.error.issues }}</pre>
            </div>

            <div
                v-else
                ref="slot"
                class="preview-game-tile"
                data-test="preview-game-tile"
            >
                <GameTile
                    :game="game"
                    :background-src="data.data.background || undefined"
                    :logo-src="data.data.logo || undefined"
                    :load-media="false"
                    eager
                />
            </div>
        </template>
    </UIApp>
</template>

<style scoped lang="scss">
.preview-game-tile {
    width: 100%;
}
</style>
